import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { AuthContext, API } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Download, FileText } from 'lucide-react';

export default function DocumentVerification() {
  const { token } = React.useContext(AuthContext);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(null);

  useEffect(() => {
    fetchPendingDocuments();
  }, []);

  const fetchPendingDocuments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/documents/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (docId, status) => {
    setVerifying(docId);
    try {
      await axios.patch(
        `${API}/documents/${docId}/verify`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Document ${status}!`);
      fetchPendingDocuments();
    } catch (error) {
      toast.error('Verification failed');
    } finally {
      setVerifying(null);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending Document Verifications</CardTitle>
      </CardHeader>
      <CardContent>
        {documents.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="w-16 h-16 text-green-300 mx-auto mb-4" />
            <p className="text-gray-600">No pending documents</p>
          </div>
        ) : (
          <div className="space-y-4">
            {documents.map((doc) => (
              <Card key={doc.id} className="border-2">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <FileText className="w-5 h-5 text-indigo-600" />
                        <div>
                          <h3 className="font-semibold">{doc.filename}</h3>
                          <p className="text-sm text-gray-500">
                            {doc.user_info?.full_name} ({doc.user_info?.email})
                          </p>
                        </div>
                      </div>
                      <Badge className="capitalize">{doc.document_type}</Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleVerify(doc.id, 'verified')}
                        disabled={verifying === doc.id}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Verify
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleVerify(doc.id, 'rejected')}
                        disabled={verifying === doc.id}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}